import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { DatePipe } from '@angular/common';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideEllipsis, lucideExternalLink, lucideMail, lucideUnlink } from '@ng-icons/lucide';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTableImports } from '@spartan-ng/helm/table';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { PartnerClient, PartnerClientStatus } from '../../../../services/partner.service';

/** How far the shop is set up, named by what's still missing. */
const SETUP: Record<
  PartnerClientStatus,
  { label: string; variant: 'default' | 'secondary' | 'outline' }
> = {
  registered: { label: 'Profil fehlt', variant: 'outline' },
  onboarding: { label: 'Zahlungen fehlen', variant: 'secondary' },
  live: { label: 'Verkaufsbereit', variant: 'default' },
};

/** Whether the customer can get into its own account, and whether the agency can. */
interface Access {
  label: string;
  hint: string;
}

/**
 * The shops the partner brought in, newest first. Each row answers two questions:
 * how far is the shop set up, and can the customer (and the agency) get in.
 */
@Component({
  selector: 'app-partner-client-list',
  standalone: true,
  imports: [
    DatePipe,
    NgIcon,
    HlmBadgeImports,
    HlmButtonImports,
    HlmDropdownMenuImports,
    HlmSpinnerImports,
    HlmTableImports,
    HlmTypographyImports,
  ],
  providers: [provideIcons({ lucideEllipsis, lucideExternalLink, lucideMail, lucideUnlink })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './partner-client-list.component.html',
})
export class PartnerClientListComponent {
  readonly clients = input.required<PartnerClient[]>();
  /** public_id of the account being opened, for its spinner. */
  readonly opening = input<string | null>(null);

  readonly openClient = output<PartnerClient>();
  readonly resendClaim = output<PartnerClient>();
  readonly leave = output<PartnerClient>();

  setup(client: PartnerClient) {
    return SETUP[client.status];
  }

  access(client: PartnerClient): Access {
    if (!client.managed) {
      return { label: 'Kein Zugriff für dich', hint: 'Hat sich über deinen Link angemeldet' };
    }
    if (client.awaits_claim) {
      return {
        label: 'Noch nicht angemeldet',
        hint: 'Link zum Passwort-Festlegen ist per E-Mail raus',
      };
    }
    return { label: 'Angemeldet', hint: 'Ihr könnt beide im Konto arbeiten' };
  }

  euro(amount: number | undefined): string {
    return (amount ?? 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
  }
}
